import React, { useCallback, useMemo, useState } from 'react';
import AvTable from '@components/Table/AvTable';
import { AvButton, AvTooltip, ColumnReorder, ExportButton, useAvContext } from '@fabric-ui/components';
import { ActionButtonV2 } from '@fabric-ui/components/Table';
import { External, X } from '@fabric-ui/icons';
import { flex, shadowPrimaryBottomSm } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { useExportQueryObjectWorkflow } from '@hooks/useExportQueryWorklflow';
import useQueryObject from '@hooks/useQueryObjectSql';
import { Box, Chip, Divider, Typography, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { PAGE_PATHS } from '../../../types';
import { getAlertsListByAgent } from './queries';
import { AgentDetailsProps } from './types';
import {
  AgentDetailAttribute,
  AgentDetailMetric,
  AgentDetailSection,
  AgentJobDescriptionSection,
  agentTableCommonStyle,
  flattenToQueryString,
  initialAgentHeadCells,
  NoDataFoundWidget,
  useAlertListHeadCells,
} from './utils';

const AgentDetails: React.FC<AgentDetailsProps> = ({ selectedAgent, setSelectedAgent, filterContext }) => {
  const theme = useTheme();

  const headCellsFromMemo = useAlertListHeadCells();

  const [hCells, setHCells] = useState(headCellsFromMemo);
  const [activeKey, setActiveKey] = useState();

  const queryObject = useMemo(() => getAlertsListByAgent(filterContext, selectedAgent.agent_id), [filterContext, selectedAgent.agent_id]);
  const queryObjectsArray = useMemo(() => [queryObject], [queryObject]);

  const { getPathName } = useAvContext();

  const { mutate: exportQueryObjectWorkflow, isPending: isExportingWorkflow } = useExportQueryObjectWorkflow();
  const { data: alertsListByAgents, isLoading: isAlertsListByAgentsDataLoading } = useQueryObject({
    queryObject: queryObjectsArray,
    options: { placeholderData: keepPreviousData },
    onSuccess: data => data ?? null,
  });

  const exportReport = useCallback(() => {
    const {
      select: { dims },
    } = queryObject;

    const enabledColumnIds = new Set(hCells.filter(cell => !cell.hidden).map(cell => cell.id));
    const queryDims = dims.filter(item => enabledColumnIds.has(item.alias));

    exportQueryObjectWorkflow({
      queryObject: {
        ...queryObject,
        select: {
          ...queryObject.select,
          dims: queryDims,
          metrics: [],
        },
        timezoneOffset: new Date().getTimezoneOffset(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  }, [queryObject, hCells]);

  const gotoEntityRowInlineAction = (row: Record<string, any>) => {
    const filter: Record<string, any> = {};
    if (row.key?.length > 0) {
      filter['alert._key'] = row.key;
    }

    const searchParams = { ...filter, ticketCategory: ['Threats'] };
    const queryString = flattenToQueryString(searchParams);
    return ActionButtonV2({
      label: `Open Alert in Alerts page`,
      onClick: () => {
        window.open(getPathName(PAGE_PATHS.ALERTS, `?${queryString}`).to, '_blank');
      },
      icon: <External />,
    });
  };

  const inlineActions = useMemo(() => [gotoEntityRowInlineAction], [gotoEntityRowInlineAction]);
  const noAlertsInvestigated = selectedAgent.cnt === 0;
  const width = 'calc(max(25%, 550px))';
  return (
    <Box
      sx={{
        ...agentTableCommonStyle,
        height: 'calc(100% - 24px)',
        boxShadow: shadowPrimaryBottomSm,
        mx: 1,
        mb: 1,
      }}>
      <Box sx={{ width, minWidth: width, ...flex.col, p: 3, pt: 2, height: '100%', overflowY: 'auto' }}>
        <Box sx={{ ...flex.itemsBaseline, justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ py: '5px', m: 0 }}>
            {selectedAgent?.name ?? ''}
          </Typography>
          <Chip
            label={selectedAgent?.category}
            size="small"
            sx={{
              color: 'var(--color-content-status-neutral-primary)',
              borderRadius: '8px',
              backgroundColor: 'var(--color-surface-status-neutral-primary)',
            }}
          />
        </Box>
        {/* Agent details Attribute Sections */}
        <AgentDetailAttribute attributes={selectedAgent.outcome} title="Output" />

        {/* Agent details Metrics Sections */}
        <Box sx={{ gap: 1, ...flex.row, justifyContent: 'space-between' }}>
          <AgentDetailMetric title="Alerts investigated" count={selectedAgent.cnt} />
        </Box>
        {/* Agent details Sections */}
        <AgentDetailSection title="Job Description" description={selectedAgent.description} />

        {/* Agent Job Description Sections */}
        <AgentJobDescriptionSection title="Work Details" actions={selectedAgent.actions} />
      </Box>

      <Divider flexItem orientation="vertical" sx={{ borderColor: 'var(--color-border-base-primary)' }} />

      <Box sx={{ flex: 1, overflow: 'hidden', p: 3, pt: 2, gap: 1, width: '100%', height: 'calc(100% - 8px)' }}>
        <Box
          sx={{
            ...(!noAlertsInvestigated ? flex.justifyBetweenStart : flex.justifyEnd),
            alignItems: 'center',
            xs: { gap: '24px' },
            [theme.breakpoints.up(1280)]: { gap: '24px' },
            [theme.breakpoints.up(1355)]: { gap: '28px' },
            [theme.breakpoints.up('laptop')]: { gap: '32px' },
            [theme.breakpoints.up(1680)]: { gap: '36px' },
            [theme.breakpoints.up('desktop')]: { gap: '40px' },
            mb: 2,
          }}>
          {!noAlertsInvestigated && (
            <Box sx={{ ...flex.itemsCenter }}>
              <Typography variant="h5">Alerts ({selectedAgent?.cnt?.toLocaleString()})</Typography>
            </Box>
          )}
          <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
            {!noAlertsInvestigated && (
              <>
                <ExportButton width={14} loading={isExportingWorkflow} onClick={exportReport} />
                <ColumnReorder items={hCells} width={16} setItems={data => setHCells(data)} initialColumns={initialAgentHeadCells} />

                <Divider orientation="vertical" variant="middle" sx={{ height: 16 }} flexItem />
              </>
            )}
            <AvTooltip title="Close Agent Details" sx={{ ml: 'auto' }}>
              <AvButton variant="filter" className="transparent" sx={{ px: 1, mr: -1 }} onClick={() => setSelectedAgent(null)}>
                <X style={iconSize(20)} />
              </AvButton>
            </AvTooltip>
          </Box>
        </Box>

        {!noAlertsInvestigated ? (
          <AvTable
            storageKey="agent_details"
            rows={alertsListByAgents ?? []}
            headCells={[...hCells]}
            loading={isAlertsListByAgentsDataLoading}
            resizable
            size="xSmall"
            allowEdit={false}
            enableSelectionColor={false}
            hasHeaderFilters={false}
            pagination
            sx={{ height: '100%' }}
            inlineActions={inlineActions}
            activeKey={activeKey}
            onActive={setActiveKey}
          />
        ) : (
          <NoDataFoundWidget />
        )}
      </Box>
    </Box>
  );
};

export default AgentDetails;
